import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  Platform,
  Image
} from 'react-native';
import { useTranslation } from 'react-i18next';
import identificationsData from '../../../../../constants/identifications.json';
import GroupId from "../../../../../../../../assets/images/group-id.png";
import RadioSelectedIcon from "../../../../../../../../assets/images/Radio-Select-Button.png";
import DrivingLicenseIcon from "../../../../../../../../assets/images/driving-license.png";
import IdCard from "../../../../../../../../assets/images/id-card.png";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";

interface IDType {
  key: string;
  EnglishLabel: string;
  SpanishLabel: string;
  Value?: string;
  photoIdCountry?: string;
  photoIdState?: string;
}

interface SenderIdentificationTypeProps {
  onSelectionChange?: (selectedId: string | null) => void;
  onContinue?: () => void;
  onValidationChange?: (isValid: boolean) => void;
}

const ReceiverIdentificationTypeStep: React.FC<SenderIdentificationTypeProps> = ({
  onSelectionChange,
  onContinue,
  onValidationChange
}) => {
  const { t, i18n } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [idTypes, setIdTypes] = useState<IDType[]>([]);
  const setSelectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).setSelectedIdTypeKey);
  const setSelectedIdTypeLabel = useSessionStore((s: SessionState) => (s as any).setSelectedIdTypeLabel);
  const setSelectedIdPhotoIdCountry = useSessionStore((s: SessionState) => (s as any).setSelectedIdPhotoIdCountry);
  const setSelectedIdPhotoIdState = useSessionStore((s: SessionState) => (s as any).setSelectedIdPhotoIdState);
  const setSelectedIdTypeValue = useSessionStore((s: SessionState) => (s as any).setSelectedIdTypeValue);

  const isValid = selectedId != null ;
    
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);
  
  useEffect(() => {
    const raw: any = identificationsData as any;
    let list: IDType[] = [];
    if (Array.isArray(raw?.IDTypes)) {
      list = (raw.IDTypes as any[]).map((x: any) => ({
        key: x.key ?? x.IDkey ?? x.Value,
        EnglishLabel: x.EnglishLabel,
        SpanishLabel: x.SpanishLabel,
        Value: x.Value,
        photoIdCountry: x.photoIdCountry,
        photoIdState: x.photoIdState,
      }));
    } else if (raw && typeof raw === 'object') {
      list = Object.entries(raw).map(([key, v]: [string, any]) => ({
        key,
        EnglishLabel: v?.EnglishLabel,
        SpanishLabel: v?.SpanishLabel,
        Value: v?.Value,
        photoIdCountry: v?.photoIdCountry,
        photoIdState: v?.photoIdState,
      }));
    }
    setIdTypes(list.filter(x => x && x.key));
  }, []);

  const handleSelection = (idKey: string) => {
    setSelectedId(idKey);
    onSelectionChange?.(idKey);
    try {
      setSelectedIdTypeKey?.(idKey);
      const selected = idTypes.find((x) => x.key === idKey);
      const label = selected ? (i18n.language === 'es' ? selected.SpanishLabel : selected.EnglishLabel) : null;
      setSelectedIdTypeLabel?.(label);
      const photoIdCountry = selected?.photoIdCountry ?? null;
      const photoIdState = selected?.photoIdState ?? null;
      setSelectedIdPhotoIdCountry?.(photoIdCountry);
      setSelectedIdPhotoIdState?.(photoIdState);
      setSelectedIdTypeValue?.(selected?.Value ?? null);
    } catch {}
  };

  const handleContinue = () => {
    if (selectedId) {
      onContinue?.();
    }
  };

  const getDisplayLabel = (idType: IDType) => {
    return i18n.language === 'es' ? idType.SpanishLabel : idType.EnglishLabel;
  };

  const getIconForIdType = (value?: string) => {
    // Return different icons based on ID type
    switch (value) {
      case 'DRV':
        return DrivingLicenseIcon; // Car icon for driver's license
      case 'PHT':
        return IdCard; // ID card icon for photo ID
      default:
        return IdCard;
    }
  };

  const Wrapper = Platform.OS === 'web' ? View : SafeAreaView;

  return (
    <Wrapper style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          {/* Header Icon */}
          <View style={styles.iconContainer}>
            <View style={styles.iconCircle}>
              <Image
                source={GroupId}
                style={{ height: 80, width: 80}}
              />
            </View>
          </View>

          {/* Title and Subtitle */}
          <Text style={styles.title}>{t('IDENTIFICATION_TYPE_TITLE')}</Text>
          <Text style={styles.subtitle}>{t('IDENTIFICATION_TYPE_SUBTITLE')}</Text>

          {/* Identification Types List */}
          <View style={styles.listContainer}>
            {idTypes.map((idType, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.listItem,
                  // selectedId === idType.IDkey && styles.selectedItem,
                ]}
                onPress={() => handleSelection(idType.key)}
              >
                <View style={styles.itemContent}>
                  <View style={styles.itemLeft}>
                    <Image
                      source={getIconForIdType(idType.Value)}
                      style={{ height: 24, width: 24}}
                    />
                    <Text style={styles.itemText}>
                      {getDisplayLabel(idType)}
                    </Text>
                  </View>
                  <View style={styles.radioContainer}>
                    <View
                      style={[
                        styles.radioButton,
                      ]}
                    >
                      {selectedId === idType.key && (
                        <Image
                          source={RadioSelectedIcon}
                          style={{ height: 20, width: 20}}   
                          resizeMode='contain'     
                        />
                      )}
                    </View>
                  </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </ScrollView>
    </Wrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingTop: 20,
    backgroundColor: '#FFFFFF',
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 30,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#2196F3',
  },
  iconText: {
    fontSize: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1F2937',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 30,
  },
  listContainer: {
    marginBottom: 20,
    width: '100%',
  },
  listItem: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
    // borderWidth: 1,
    borderBottomWidth:1,
    borderColor: '#D1D1D1',
  },
  selectedItem: {
    borderColor: '#3B82F6',
    backgroundColor: '#F8FAFF',
  },
  itemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  itemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  itemIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  itemText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1F2937',
    flex: 1,
    paddingLeft: 20
  },
  radioContainer: {
    marginLeft: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#D1D1D1'
  },
  buttonContainer: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    paddingTop: 10,
  },
  continueButton: {
    backgroundColor: '#1F2937',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  continueButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ReceiverIdentificationTypeStep;
